"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.jsonGetRenderedDocB64OrNull = exports.getRenderedDocB64OrNull = exports.getTemplatedSpelUsedInDocument = exports.fullyRenderDocument = void 0;
/* eslint-disable @typescript-eslint/no-explicit-any */
var Either_1 = require("fp-ts/lib/Either");
var angular_parser_1 = require("../angular-parser");
var evaluate_1 = require("../../spel/evaluate");
var abab_1 = require("abab");
var jsog_1 = __importDefault(require("jsog"));
var clone_1 = __importDefault(require("clone"));
var jszip_1 = __importDefault(require("jszip"));
var docxtemplater_1 = __importDefault(require("docxtemplater"));
var docxtemplater_image_module_free_1 = __importDefault(require("docxtemplater-image-module-free"));
var utils_1 = require("../../spel/evaluation-context/utils");
var merge_data_1 = require("../../merge-data");
var evaluation_context_1 = require("../../spel/evaluation-context");
var serializeAllDateObjects_1 = __importDefault(require("./util/serializeAllDateObjects"));
var DEFAULT_DOCX_MIMETYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
var base64Regex = /^data:image\/(png|jpg|jpeg|svg|svg\+xml);base64,/;
function base64Parser(dataURL) {
    if (typeof dataURL !== "string" ||
        !base64Regex.test(dataURL)) {
        return false;
    }
    var stringBase64 = dataURL.replace(base64Regex, "");
    // For nodejs, return a Buffer
    if (typeof Buffer !== "undefined" && Buffer.from) {
        return Buffer.from(stringBase64, "base64");
    }
    // For browsers, return a string (of binary content) :
    var binaryString = (0, abab_1.atob)(stringBase64);
    var len = binaryString.length;
    var bytes = new Uint8Array(len);
    for (var i = 0; i < len; i++) {
        var ascii = binaryString.charCodeAt(i);
        bytes[i] = ascii;
    }
    return bytes.buffer;
}
function getPngDimensions(base64) {
    var b64AfterHeader = base64.split(',', 2)[1];
    var atobImpl = (function () {
        if (typeof Buffer !== "undefined" && Buffer.from) {
            return function (b64Data) { return Buffer.from(b64Data, 'base64').toString('binary'); };
        }
        return abab_1.atob;
    })();
    var dataHeader = b64AfterHeader.slice(0, 50);
    var uint8 = Uint8Array.from(atobImpl(dataHeader), function (c) { return c.charCodeAt(0); });
    var dataView = new DataView(uint8.buffer, 0, 28);
    return {
        width: dataView.getInt32(16),
        height: dataView.getInt32(20)
    };
}
var opts = {
    centered: false,
    getImage: function (tagValue) {
        if ((tagValue.sizePixel || tagValue.maxSize) && tagValue.data) {
            return base64Parser(tagValue.data);
        }
        return base64Parser(tagValue);
    },
    getSize: function (img, tagValue) {
        var base64 = typeof tagValue === 'string' ? tagValue : tagValue.data;
        var isPng = base64.startsWith('data:image/png;base64,');
        if (isPng) {
            var _a = getPngDimensions(base64), width = _a.width, height = _a.height;
            if (typeof tagValue === 'string' || !tagValue.maxSize) {
                return [width, height];
            }
            var maxWidth = tagValue.maxSize[0];
            var maxHeight = tagValue.maxSize[1];
            var widthRatio = width / maxWidth;
            var heightRatio = height / maxHeight;
            if (widthRatio < 1 && heightRatio < 1) {
                /*
                * Do not scale up images that are
                * smaller than maxWidth,maxHeight
                */
                return [width, height];
            }
            var finalWidth = void 0, finalHeight = void 0;
            if (widthRatio > heightRatio) {
                /*
                * Width will be equal to maxWidth
                * because width is the most "limiting"
                */
                finalWidth = maxWidth;
                finalHeight = height / widthRatio;
            }
            else {
                /*
                * Height will be equal to maxHeight
                * because height is the most "limiting"
                */
                finalHeight = maxHeight;
                finalWidth = width / heightRatio;
            }
            return [Math.round(finalWidth), Math.round(finalHeight)];
        }
        if (tagValue.sizePixel && tagValue.data) {
            return tagValue.sizePixel;
        }
        return [150, 150];
    },
};
var DocumentRenderer = /** @class */ (function () {
    function DocumentRenderer(args) {
        var _this = this;
        this.getZipFromB64 = function (b64) {
            var byteChars = (0, abab_1.atob)(b64);
            return new _this.deps.JSZIP(byteChars, { type: 'uint8array' });
        };
        this.getData = function () {
            var _a = _this, entityDataGraph = _a.entityDataGraph, _b = _a.taskContext, formContext = _b.formContext, taskInfo = _b.taskInfo;
            var clonedEntityGraph = _this.deps.clone(entityDataGraph, true);
            clonedEntityGraph.form_data = _this.deps.clone(formContext);
            clonedEntityGraph._task = _this.deps.clone(taskInfo);
            return (0, serializeAllDateObjects_1.default)(clonedEntityGraph);
        };
        this.getMimeType = function () { return _this.options.generatedDocumentMimeType || DEFAULT_DOCX_MIMETYPE; };
        this.toEither = function () {
            var renderStatus = _this.renderStatus;
            if (renderStatus.status === 'RENDERED') {
                var subStatus = renderStatus.subStatus;
                if (subStatus.status === 'OUTPUT_GENERATED') {
                    return (0, Either_1.right)(renderStatus);
                }
                return (0, Either_1.left)("Render succeeded but no output could be generated of type \"".concat(_this.options.output, "\""));
            }
            else if (renderStatus.status === 'RENDER_FAILED') {
                return (0, Either_1.left)(renderStatus.errorObject);
            }
            return (0, Either_1.left)('render was not executed');
        };
        this.renderStatus = {
            status: 'NOT_RENDERED',
        };
        this.options = args.options;
        this.deps = args.deps;
        this.b64DocxTemplate = args.b64DocxTemplate;
        this.taskContext = args.taskContext;
        this.entityDataGraph = args.entityDataContext.rootObject;
    }
    DocumentRenderer.prototype.render = function () {
        var doc = new this.deps.DocxTemplater()
            .setOptions({
            parser: this.deps.parser,
            linebreaks: true,
            nullGetter: function () { return ''; },
        })
            .loadZip(this.getZipFromB64(this.b64DocxTemplate))
            .attachModule(new docxtemplater_image_module_free_1.default(opts));
        var data = this.getData();
        if (!process || !process.env || process.env.NODE_ENV !== 'test') {
            console.log('data applied to print-template', data);
        }
        doc.setData(data);
        try {
            doc.render();
            this.renderStatus = {
                status: 'RENDERED',
                renderedDocumentText: doc.getFullText(),
                subStatus: {
                    status: 'NO_OUTPUT_GENERATED',
                },
            };
        }
        catch (e) {
            this.renderStatus = {
                status: 'RENDER_FAILED',
                errorObject: e,
                message: 'exception thrown while attempting document render with data',
            };
        }
        if (this.renderStatus.status === 'RENDERED') {
            var zip = doc.getZip();
            var output = zip.generate({
                type: this.options.output,
                mimeType: this.getMimeType(),
                compression: 'DEFLATE',
            });
            this.renderStatus = __assign(__assign({}, this.renderStatus), { subStatus: {
                    status: 'OUTPUT_GENERATED',
                    renderedDocumentOutput: output,
                } });
        }
        return this;
    };
    return DocumentRenderer;
}());
exports.default = DocumentRenderer;
var DefaultDeps = {
    clone: clone_1.default,
    JSZIP: jszip_1.default,
    DocxTemplater: docxtemplater_1.default,
    spelEvaluator: evaluate_1.evaluateExpression
};
var fullyRenderDocument = function (deps) {
    if (deps === void 0) { deps = DefaultDeps; }
    return function (taskContext, entityDataContext, documentB64, finalOutput) {
        var _a;
        if (finalOutput === void 0) { finalOutput = 'base64'; }
        var evaluateSpel = (_a = deps.spelEvaluator) !== null && _a !== void 0 ? _a : evaluate_1.evaluateExpression;
        var _createSPELContext = function () {
            return entityDataContext.normalizedEntities && entityDataContext.viewConfig
                ? __assign({ lookupEntityData: (0, utils_1.getLookupEntityField)(entityDataContext.viewConfig, entityDataContext.normalizedEntities), containsCodes: (0, utils_1.getContainsCodes)(entityDataContext.normalizedEntities.Concept || {}) }, (0, evaluation_context_1.createRootContext)({})) : __assign({}, (0, evaluation_context_1.createRootContext)({}));
        };
        var _getRenderedDocument = function (mode, _documentB64, output) {
            var DR = new DocumentRenderer({
                deps: __assign(__assign({}, deps), { parser: mode === 'EVAL_SPEL'
                        ? (0, angular_parser_1.getAngularParser)(mode, function (exp) { var _a; return "".concat((_a = evaluateSpel(exp, _createSPELContext(), _createSPELContext())) !== null && _a !== void 0 ? _a : ''); }).parser
                        : (0, angular_parser_1.getAngularParser)(mode).parser }),
                b64DocxTemplate: _documentB64,
                entityDataContext: entityDataContext,
                taskContext: taskContext,
                options: {
                    output: output,
                },
            });
            return DR;
        };
        return _getRenderedDocument('ESCAPE_SPEL', documentB64, 'base64')
            .render()
            .toEither()
            .chain(function (successState) {
            return _getRenderedDocument('EVAL_SPEL', successState.subStatus.renderedDocumentOutput, finalOutput)
                .render()
                .toEither();
        });
    };
};
exports.fullyRenderDocument = fullyRenderDocument;
var getTemplatedSpelUsedInDocument = function (deps) {
    if (deps === void 0) { deps = DefaultDeps; }
    return function (taskContext, entityDataContext, documentB64) {
        var getSpelCollected;
        var _getRenderedDocumentB64 = function (_documentB64) {
            var DR = new DocumentRenderer({
                deps: __assign(__assign({}, deps), { parser: (function () {
                        var p = (0, angular_parser_1.getAngularParser)('ESCAPE_SPEL');
                        getSpelCollected = p.getSpelCollectedByParser;
                        return p.parser;
                    })() }),
                b64DocxTemplate: _documentB64,
                entityDataContext: entityDataContext,
                taskContext: taskContext,
                options: {
                    output: 'base64',
                },
            });
            return DR;
        };
        return (_getRenderedDocumentB64(documentB64)
            .render()
            .toEither()
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            .chain(function (successState) {
            if (getSpelCollected) {
                return (0, Either_1.right)(getSpelCollected());
            }
            return (0, Either_1.left)(new Error('some unknown error occurred'));
        }));
    };
};
exports.getTemplatedSpelUsedInDocument = getTemplatedSpelUsedInDocument;
var getRenderedDocB64OrNull = function (allDataJson, documentB64, viewConfig, rootObject, formData, taskInfo, processVariables) {
    if (formData === void 0) { formData = {}; }
    if (taskInfo === void 0) { taskInfo = {}; }
    if (processVariables === void 0) { processVariables = {}; }
    var normalizedEntities = (0, merge_data_1.getMergeDataFromMultipleSources)(viewConfig)(__spreadArray([jsog_1.default.encode(rootObject)], allDataJson, true));
    return (0, exports.fullyRenderDocument)()({
        formContext: formData,
        processVariables: processVariables,
        taskInfo: taskInfo,
    }, {
        rootObject: jsog_1.default.decode(rootObject),
        normalizedEntities: normalizedEntities,
        viewConfig: viewConfig,
    }, documentB64, 'base64')
        .map(function (rs) { return rs.subStatus.renderedDocumentOutput; })
        .getOrElse(null); // silently swallows error
};
exports.getRenderedDocB64OrNull = getRenderedDocB64OrNull;
var jsonGetRenderedDocB64OrNull = function (allDataJson, documentB64, viewConfig, rootObject, formData, taskInfo, processVariables) {
    return (0, exports.getRenderedDocB64OrNull)(JSON.parse(allDataJson), documentB64, JSON.parse(viewConfig), JSON.parse(rootObject), formData && JSON.parse(formData), taskInfo && JSON.parse(taskInfo), processVariables && JSON.parse(processVariables));
};
exports.jsonGetRenderedDocB64OrNull = jsonGetRenderedDocB64OrNull;
//# sourceMappingURL=renderDocument.js.map